import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { m as motion } from "framer-motion";
import {
  Calendar,
  CheckCircle2,
  Linkedin,
  Play,
  Sparkles,
  Youtube,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { VideoLightbox } from "@/components/video-lightbox";
import { buildWebpSrcSet } from "@/lib/image-srcset";
import { NEXT_COHORT, NEXT_COHORT_PATH } from "@/lib/cohorts";
import { defaultViewport } from "@/lib/motion";
import { breadcrumb, educationEvent } from "@/lib/seo-schemas";
import { useSEO } from "@/lib/use-seo";
import {
  getTitledHighlights,
  getUntitledHighlightUrls,
  getWorkshopBySlug,
  getWorkshopPath,
} from "@/lib/workshops";

const MEMBERSHIP_URL = "https://members.aiforhrmastermind.com/memberships";

const WorkshopPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const workshop = slug ? getWorkshopBySlug(slug) : undefined;
  const path = workshop ? getWorkshopPath(workshop.slug) : "/workshops";
  const isPast = workshop?.isPast ?? false;
  const seoDescription =
    workshop?.seoDescription ??
    workshop?.shortDescription.slice(0, 160) ??
    "AI for HR Mastermind workshops";

  useSEO({
    title: workshop
      ? `${workshop.title} — ${isPast ? "Workshop Recording" : "Live Session"}`
      : "Workshop — AI for HR Mastermind",
    description: seoDescription,
    path,
    ogImage: workshop?.ogImage ?? workshop?.featuredImage,
    jsonLd: workshop?.fullDescription
      ? [
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Workshops", path: "/workshops" },
            { name: workshop.title, path },
          ]),
          educationEvent({
            name: workshop.title,
            description: seoDescription,
            startDate: workshop.dateTimeIso,
            endDate: workshop.endDateTimeIso,
            url: `https://www.aiforhrmastermind.com${path}`,
            eventStatus: isPast
              ? "https://schema.org/EventCompleted"
              : "https://schema.org/EventScheduled",
          }),
        ]
      : undefined,
  });

  if (!workshop?.fullDescription) {
    return <Navigate to="/workshops" replace />;
  }

  const registrationUrl = workshop.registrationLink;
  const recordingUrl = workshop.registrationLink ?? MEMBERSHIP_URL;
  const titledHighlights = getTitledHighlights(workshop.videoHighlights);
  const untitledHighlightUrls = getUntitledHighlightUrls(
    workshop.videoHighlights,
  );
  const [introParagraph, workshopParagraph] = workshop.fullDescription.paragraphs;
  const linkedInTrimMinutes = workshop.linkedInPublicTrimMinutes;
  const showLinkedInTrim =
    Boolean(isPast && linkedInTrimMinutes && workshop.linkedInEventUrl);
  const hasLiveLinks = Boolean(
    workshop.linkedInEventUrl || workshop.youtubeLiveUrl,
  );
  const joinLinksPending = !isPast && !hasLiveLinks && !registrationUrl;

  return (
    <div className="min-h-screen pt-32">
      <VideoLightbox videoUrl={activeVideo} onClose={() => setActiveVideo(null)} />

      {/* Hero */}
      <section className="section-padding pb-10 md:pb-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-20 pointer-events-none" />

        <div className="section-inner relative z-10 max-w-3xl mx-auto">
          <Link
            to="/workshops"
            className="inline-block text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            ← Back to Workshops
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="space-y-5"
          >
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground/70">
                {isPast ? "Past Workshop" : workshop.category ?? "Workshop"}
              </span>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                {workshop.date}
              </span>
              <span aria-hidden="true">·</span>
              <span>60 minutes</span>
            </p>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              {workshop.title}
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {workshop.shortDescription}
            </p>

            {workshop.speaker && (
              <p className="text-sm font-medium text-foreground/80">
                Led by {workshop.speaker}
                {workshop.speakerTitle && ` · ${workshop.speakerTitle}`}
              </p>
            )}
          </motion.div>

          {workshop.featuredImage && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className="mt-8 rounded-2xl overflow-hidden border border-border/50 bg-muted/20"
            >
              <img
                src={workshop.featuredImage}
                srcSet={buildWebpSrcSet(workshop.featuredImage)}
                sizes="(min-width: 1024px) 768px, 100vw"
                alt={`${workshop.title} workshop cover illustration`}
                className="w-full h-auto"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.12 }}
            className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3"
          >
            {isPast ? (
              <>
                <Button asChild size="lg" className="sm:flex-1 sm:max-w-xs">
                  <Link to={NEXT_COHORT_PATH}>
                    See the {NEXT_COHORT.monthYearLabel} cohort
                  </Link>
                </Button>
                <Button variant="outline" asChild size="lg">
                  <a
                    href={recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Play className="h-4 w-4 mr-2" aria-hidden="true" />
                    Members: watch the full recording
                  </a>
                </Button>
                {!showLinkedInTrim && workshop.youtubeLiveUrl && (
                  <Button variant="outline" asChild size="lg">
                    <a
                      href={workshop.youtubeLiveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Youtube className="h-4 w-4 mr-2" aria-hidden="true" />
                      Watch on YouTube
                    </a>
                  </Button>
                )}
              </>
            ) : joinLinksPending ? (
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
                A 60-minute session for HR. Start with you and your work. Let AI
                take the constraints off. LinkedIn Live and YouTube links coming
                soon.
              </p>
            ) : (
              <>
                {workshop.linkedInEventUrl && (
                  <Button asChild size="lg" className="sm:flex-1 sm:max-w-xs">
                    <a
                      href={workshop.linkedInEventUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Linkedin className="h-4 w-4 mr-2" aria-hidden="true" />
                      Watch on LinkedIn Live
                    </a>
                  </Button>
                )}
                {workshop.youtubeLiveUrl && (
                  <Button variant="outline" asChild size="lg">
                    <a
                      href={workshop.youtubeLiveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Youtube className="h-4 w-4 mr-2" aria-hidden="true" />
                      Watch on YouTube
                    </a>
                  </Button>
                )}
              </>
            )}
          </motion.div>
          {isPast && (
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              {NEXT_COHORT.monthYearLabel} cohort starts {NEXT_COHORT.startDateLabel}.
              {titledHighlights.length > 0
                ? " Public highlight clips are on this page."
                : null}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="section-padding pt-0 pb-16 md:pb-20">
        <div className="section-inner max-w-3xl mx-auto space-y-6">
          {isPast && titledHighlights.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={defaultViewport}
              transition={{ duration: 0.45 }}
              className="space-y-5"
            >
              <div>
                <h2 className="text-lg font-semibold mb-2">Workshop highlights</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Three clips from the session. Play them here. The full recording
                  is for paying AI for HR Mastermind members.
                </p>
              </div>
              {titledHighlights.map((clip) => (
                <figure key={clip.url} className="space-y-2">
                  <figcaption className="text-sm font-medium text-foreground">
                    {clip.title}
                  </figcaption>
                  <video
                    src={clip.url}
                    className="w-full rounded-xl bg-muted/40 aspect-video"
                    controls
                    playsInline
                    preload="metadata"
                  >
                    <a href={clip.url}>Download {clip.title}</a>
                  </video>
                </figure>
              ))}
            </motion.div>
          )}

          {introParagraph && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={defaultViewport}
              transition={{ duration: 0.45 }}
              className="glass rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-lg font-semibold mb-3">Why this matters</h2>
              <p className="text-foreground/85 leading-relaxed">
                {introParagraph}
              </p>
            </motion.div>
          )}

          {workshopParagraph && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={defaultViewport}
              transition={{ duration: 0.45, delay: 0.05 }}
              className="glass rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-lg font-semibold mb-3">About this workshop</h2>
              <p className="text-foreground/85 leading-relaxed">
                {workshopParagraph}
              </p>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="glass rounded-2xl p-6 md:p-8"
          >
            <h2 className="text-lg font-semibold mb-4">
              {isPast ? "What we covered" : "What you'll do"}
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              {isPast
                ? "In this session, we:"
                : "During this interactive workshop, you will:"}
            </p>
            <ul className="space-y-3">
              {workshop.fullDescription.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-3 text-foreground/85">
                  <CheckCircle2
                    className="h-5 w-5 text-primary shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span className="leading-relaxed">{bullet}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {isPast && untitledHighlightUrls.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={defaultViewport}
              transition={{ duration: 0.45, delay: 0.12 }}
              className="glass rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-lg font-semibold mb-4">Workshop highlights</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {untitledHighlightUrls.map((videoUrl) => (
                  <button
                    key={videoUrl}
                    type="button"
                    onClick={() => setActiveVideo(videoUrl)}
                    className="relative aspect-[9/16] rounded-lg bg-muted/50 border border-border/50 overflow-hidden group"
                    aria-label="Play workshop highlight clip"
                  >
                    <video
                      src={`${videoUrl}#t=0.5`}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      crossOrigin="anonymous"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-foreground/30 group-hover:bg-foreground/50 transition-colors">
                      <span className="w-12 h-12 rounded-full bg-background/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play
                          className="h-5 w-5 text-primary ml-0.5"
                          fill="currentColor"
                          aria-hidden="true"
                        />
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.45, delay: 0.15 }}
            className="glass rounded-2xl p-6 md:p-8"
          >
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-secondary" aria-hidden="true" />
              {isPast ? "What attendees left with" : "What you'll leave with"}
            </h2>
            <p className="text-foreground/85 leading-relaxed">
              {workshop.fullDescription.closing}
            </p>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="glass-strong rounded-2xl p-6 md:p-8"
            aria-label="Workshop access details"
          >
            {isPast ? (
              <>
                <h2 className="text-lg font-semibold mb-2">
                  Full recording and next cohort
                </h2>
                <p className="text-foreground/85 leading-relaxed mb-5">
                  This workshop has already run. Watch the highlight clips on
                  this page. Paying AI for HR Mastermind members can watch the
                  full recording in Disco. The {NEXT_COHORT.monthYearLabel}{" "}
                  cohort starts {NEXT_COHORT.startDateLabel}.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-5">
                  <Button asChild size="lg">
                    <Link to={NEXT_COHORT_PATH}>
                      See the {NEXT_COHORT.monthYearLabel} cohort
                    </Link>
                  </Button>
                  <Button variant="outline" asChild size="lg">
                    <a
                      href={recordingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Play className="h-4 w-4 mr-2" aria-hidden="true" />
                      Members: watch the full recording
                    </a>
                  </Button>
                </div>
                {showLinkedInTrim && workshop.linkedInEventUrl && (
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    LinkedIn has a {linkedInTrimMinutes}-minute public clip, not
                    the full session.{" "}
                    <a
                      href={workshop.linkedInEventUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      Watch the {linkedInTrimMinutes}-minute clip on LinkedIn
                    </a>
                    .
                  </p>
                )}
              </>
            ) : joinLinksPending ? (
              <>
                <h2 className="text-lg font-semibold mb-2">How to join</h2>
                <p className="text-foreground/85 leading-relaxed mb-4">
                  Watch live on LinkedIn (primary public stream). YouTube backup
                  if LinkedIn Live has issues. Interactive Zoom via AI for HR
                  Mastermind registration (limited seats).
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Registration and livestream links are not published yet. Check
                  back here or on the workshops page when they are ready.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold mb-2">
                  Join live or build along
                </h2>
                <p className="text-foreground/85 leading-relaxed mb-5">
                  Want to ask questions live and build alongside us? Register for
                  the interactive Zoom session.
                </p>
                {registrationUrl && (
                  <Button asChild size="lg" className="mb-5">
                    <a
                      href={registrationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Users className="h-4 w-4 mr-2" aria-hidden="true" />
                      Register for interactive Zoom
                    </a>
                  </Button>
                )}
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Free to watch live. The replay stays free for 48 hours, then
                  becomes available to AI for HR Mastermind members.
                </p>
              </>
            )}
          </motion.aside>
        </div>
      </section>
    </div>
  );
};

export default WorkshopPage;
